'use client';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@ui/icons';
import { Checkbox, ButtonLink } from '@web/atoms';
import { ResponsiveImage } from '@web/molecules';
import {
  StyledImageDropdown,
  DropdownButton,
  DropdownMenu,
  IconContainer,
  DropdownContentContainer,
  ContentHeaderContainer,
  HeaderText,
  HelperText,
  StyledDropdownImageItem,
  DropdownItemContent,
  ContentImage,
  ContentText,
  ContentTextContainer,
  ContentLink,
  SelectionRow,
  SelectedText,
  RedDot,
  AllergyWarningText,
  LimitWarningText,
} from './styles.js';

export const ImageDropdown = ({
  heading,
  helperText,
  imageSrc,
  altText,
  defaultText = 'No selection',
  maxSelections = 4,
  defaultSelectedIds = [],
  children,
  onClickMenuButton,
  onChange,
  onDetailsClick,
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds);

  const handleOpenMenu = () => {
    if (!openMenu) {
      onClickMenuButton && onClickMenuButton();
    }
    setOpenMenu(!openMenu);
  };

  const handleItemToggle = id => {
    let newSelectedIds = selectedIds;

    if (selectedIds.includes(id)) {
      newSelectedIds = selectedIds.filter(selectedId => selectedId !== id);
    } else {
      if (selectedIds.length < maxSelections) {
        newSelectedIds = [...selectedIds, id];
      }
    }

    setSelectedIds(newSelectedIds);

    if (onChange) {
      onChange(newSelectedIds);
    }
  };

  const getSelectedText = () => {
    if (selectedIds.length === 0) return defaultText;

    const selectedLabels = React.Children.toArray(children)
      .filter(child => selectedIds.includes(child.props.id))
      .map(child => child.props.summaryLabel || child.props.heading);

    return selectedLabels.join(', ');
  };

  const isLimitReached = selectedIds.length >= maxSelections;

  return (
    <StyledImageDropdown>
      <DropdownContentContainer>
        <ContentHeaderContainer>
          {heading && <HeaderText>{heading}</HeaderText>}
          {helperText && <HelperText>{helperText}</HelperText>}
        </ContentHeaderContainer>
        <SelectionRow>
          <ResponsiveImage
            src={imageSrc}
            alt={altText}
            height={48}
            width={75}
          />
          <SelectedText>{getSelectedText()}</SelectedText>
        </SelectionRow>
      </DropdownContentContainer>
      <DropdownButton onClick={handleOpenMenu}>
        <IconContainer>
          {openMenu ? (
            <Icon iconName="chevron-up" alt="" size={1} id="up_dropdown_menu" />
          ) : (
            <Icon
              iconName="chevron-down"
              alt=""
              size={1}
              id="down_dropdown_menu"
            />
          )}
        </IconContainer>
      </DropdownButton>
      {openMenu && (
        <DropdownMenu>
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              const isSelected = selectedIds.includes(child.props.id);
              const isAllergen = child.props.isAllergen;
              const isLimitDisabled =
                !isSelected && isLimitReached && !isAllergen;
              const isDisabled = isAllergen || isLimitDisabled;

              return React.cloneElement(child, {
                checked: isSelected,
                disabled: isDisabled,
                isLimitDisabled: isLimitDisabled,
                isAllergen: isAllergen,
                onDetailsClick: onDetailsClick,
                onChange: () => !isDisabled && handleItemToggle(child.props.id),
              });
            }
            return child;
          })}
        </DropdownMenu>
      )}
    </StyledImageDropdown>
  );
};

ImageDropdown.propTypes = {
  heading: PropTypes.string,
  helperText: PropTypes.string,
  imageSrc: PropTypes.string,
  altText: PropTypes.string,
  defaultText: PropTypes.string,
  maxSelections: PropTypes.number,
  defaultSelectedIds: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
  onClickMenuButton: PropTypes.func,
  onChange: PropTypes.func,
  onDetailsClick: PropTypes.func,
};

export const DropdownImageItem = ({
  id,
  heading,
  summaryLabel,
  type,
  linkText,
  link,
  imageSrc,
  altText,
  checked,
  disabled,
  isLimitDisabled,
  isAllergen,
  onChange,
  onDetailsClick,
}) => {
  const renderBottomContent = () => {
    if (isAllergen) {
      return (
        <AllergyWarningText>
          <RedDot /> Unavailable due to allergy concern
        </AllergyWarningText>
      );
    }

    if (isLimitDisabled) {
      return (
        <LimitWarningText>
          Remove a selection to choose this item
        </LimitWarningText>
      );
    }

    return (
      <ButtonLink
        variant="hero.plum"
        onClick={e => {
          e.stopPropagation();
          if (onDetailsClick) {
            onDetailsClick({ id, link, type }); //* May not use a link but if we do, we can pass it through the onDetailsClick handler
          }
        }}
      >
        {linkText}
      </ButtonLink>
    );
  };

  return (
    <StyledDropdownImageItem $disabled={disabled} $isAllergen={isAllergen}>
      <DropdownItemContent onClick={onChange}>
        <div onClick={e => e.stopPropagation()}>
          <Checkbox
            key={id}
            name={heading}
            checked={checked}
            disabled={disabled}
            onChange={onChange}
          />
        </div>

        <ContentImage>
          <ResponsiveImage
            src={imageSrc}
            alt={altText}
            height={48}
            width={65}
          />
        </ContentImage>
        <ContentTextContainer>
          <ContentText>{heading}</ContentText>
          <ContentLink>{renderBottomContent()}</ContentLink>
        </ContentTextContainer>
      </DropdownItemContent>
    </StyledDropdownImageItem>
  );
};

DropdownImageItem.propTypes = {
  id: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  summaryLabel: PropTypes.string,
  type: PropTypes.string,
  linkText: PropTypes.string.isRequired,
  link: PropTypes.string,
  imageSrc: PropTypes.string.isRequired,
  altText: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  isLimitDisabled: PropTypes.bool,
  isAllergen: PropTypes.bool,
  onChange: PropTypes.func,
  onDetailsClick: PropTypes.func,
};
